// Remark plugin that rewrites inline placeholder tokens in docs markdown into
// custom elements, which DocsArticle maps to React components that render the
// signed-in user's real addresses. Keeping this as a token swap (rather than
// MDX) lets the docs route stay lightweight and code-split.
//
// Supported tokens:
//   {{kindle_email}}        -> inline <inbound-email kind="kindle">
//   {{save_email}}          -> inline <inbound-email kind="save">
//   {{save_folder_emails}}  -> block  <folder-emails> (a table; unwrapped from <p>)

interface MdastNode {
  type: string;
  value?: string;
  children?: MdastNode[];
  data?: { hName?: string; hProperties?: Record<string, unknown> };
}

// Maps each token to the custom element name DocsArticle renders.
// save_folder_emails is block-level (a table, unwrapped from <p>); the others
// are inline sentences.
const TOKEN_TO_HNAME: Record<string, string> = {
  save_folder_emails: 'folder-emails',
  kindle_email_step: 'kindle-email-step',
  save_email_intro: 'save-email-intro',
  press_brand_assets: 'press-brand-assets',
};

const TOKEN_SOURCE = '\\{\\{(' + Object.keys(TOKEN_TO_HNAME).join('|') + ')\\}\\}';

const BLOCK_HNAMES = new Set(['folder-emails', 'press-brand-assets']);

function tokenNode(token: string): MdastNode {
  return {
    type: 'inboundEmail',
    data: { hName: TOKEN_TO_HNAME[token], hProperties: {} },
    children: [],
  };
}

function splitTextNode(node: MdastNode): MdastNode[] {
  const value = node.value ?? '';
  const regex = new RegExp(TOKEN_SOURCE, 'g');
  const result: MdastNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(value)) !== null) {
    if (match.index > lastIndex) {
      result.push({ type: 'text', value: value.slice(lastIndex, match.index) });
    }
    result.push(tokenNode(match[1]));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < value.length) {
    result.push({ type: 'text', value: value.slice(lastIndex) });
  }

  return result;
}

function isBlankText(node: MdastNode): boolean {
  return node.type === 'text' && (node.value ?? '').trim() === '';
}

function isBlockToken(node: MdastNode): boolean {
  return !!node.data?.hName && BLOCK_HNAMES.has(node.data.hName);
}

// Expand tokens within a node's children. Paragraphs that resolve to a single
// block-level token are unwrapped, since a table can't be nested inside the
// <p> react-markdown would otherwise emit.
function transform(node: MdastNode): MdastNode[] {
  if (!node.children || node.children.length === 0) return [node];

  const nextChildren: MdastNode[] = [];
  for (const child of node.children) {
    if (child.type === 'text' && (child.value ?? '').includes('{{')) {
      nextChildren.push(...splitTextNode(child));
    } else {
      nextChildren.push(...transform(child));
    }
  }
  node.children = nextChildren;

  if (node.type === 'paragraph') {
    const meaningful = nextChildren.filter((child) => !isBlankText(child));
    if (meaningful.length === 1 && isBlockToken(meaningful[0])) {
      return [meaningful[0]];
    }
  }

  return [node];
}

export default function remarkDocTokens() {
  return (tree: MdastNode): void => {
    transform(tree);
  };
}
